import {
  useState,
  useEffect,
  useRef,
  useCallback,
  DragEvent,
  ChangeEvent,
} from 'react';
import {
  Modal,
  Backdrop,
  Box,
  Typography,
  IconButton,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import TextInput from 'src/components/TextInput';
import Checkbox from 'src/components/Checkbox';
import { Preference, PreferenceData } from 'src/types/preference';
import { AlignmentType } from 'src/types/campaign';
import {
  createPreference,
  updatePreference,
  normalizeIabTopExclusionRules,
  normalizeGenreRiskOverrides,
  normalizeGenreExclusionBypassRules,
} from 'src/apis/preference';
import useNotification from 'src/hooks/useNotification';
import useDashboardFilters from 'src/hooks/useDashboardFilters';
import { getFilteredGarmShowNames } from 'src/apis/garm';
import { GenreSelector } from './GenreSelector';
import { IABSelector } from './IABSelector';
import { ShowSelector } from './ShowSelector';
import {
  RiskColumnSelector,
  RISK_CATEGORY_OPTIONS,
} from './RiskColumnSelector';
import { LanguageSelector } from './LanguageSelector';
import { All_Languages } from 'src/containers/sidebar/LanguageFilter';
import { ReactComponent as UploadIcon } from 'src/assets/icons/uploadCloud.svg';
import { PreferenceShowCsvImportModal } from './PreferenceShowCsvImportModal';

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    content: {
      width: '100%',
      maxHeight: 600,
      overflowY: 'auto',
      marginTop: 20,
    },
    buttons: {
      paddingTop: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      justifyContent: 'flex-end',
    },
    inputField: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      marginBottom: 16,
    },
    description: {
      padding: '10px 14px',
      borderRadius: 8,
      border: '1px solid #C3C3C7',
      width: '100%',
      '& .MuiInputBase-root:before, & .MuiInputBase-root:after': {
        border: 'none !important',
      },
    },
    section: {
      marginBottom: 24,
      padding: 16,
      border: '1px solid #EAECF0',
      borderRadius: 8,
      background: '#F9FAFB',
    },
    sectionTitle: {
      marginBottom: 12,
      fontWeight: 600,
    },
    selectorButton: {
      padding: '10px 14px',
      borderRadius: 8,
      width: '100%',
      height: '52px',
      marginTop: 4,
      justifyContent: 'flex-start',
      border: '1px solid #D0D5DD',
      background: '#FFF',
    },
    ruleItem: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: 8,
      padding: 12,
      background: '#FFF',
      border: '1px solid #EAECF0',
      borderRadius: 8,
      marginBottom: 8,
    },
    ruleField: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      minWidth: 0,
    },
    ruleFieldLabel: {
      fontSize: 12,
      color: '#667085',
      fontWeight: 500,
    },
    ruleInput: {
      flex: 1,
      padding: '8px 12px',
      border: '1px solid #D0D5DD',
      borderRadius: 6,
      fontSize: 14,
    },
    riskOverrideItem: {
      padding: 12,
      background: '#FFF',
      border: '1px solid #EAECF0',
      borderRadius: 8,
      marginBottom: 12,
    },
    riskOverrideHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 8,
    },
    riskOverrideColumns: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      marginTop: 8,
    },
    columnChip: {
      padding: '4px 8px',
      background: '#F2F4F7',
      borderRadius: 4,
      fontSize: 12,
    },
    selectedItems: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      marginTop: 8,
    },
    selectedItem: {
      padding: '4px 8px',
      background: '#ECFDF3',
      border: '1px solid #5EA1F2',
      borderRadius: 4,
      fontSize: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
    removeButton: {
      cursor: 'pointer',
      color: '#B42318',
      fontSize: 14,
    },
    dragDropArea: {
      border: '1px dashed #EAECF0',
      borderRadius: 8,
      padding: 24,
      textAlign: 'center',
      cursor: 'pointer',
      background: '#FFF',
      marginBottom: 16,
      '&.drag-active': {
        background: '#F7F7F7',
        borderColor: '#5EA1F2',
      },
    },
    uploadIcon: {
      marginBottom: 8,
      width: 42,
      height: 42,
      borderRadius: 8,
      border: '1px solid #EAECF0',
      padding: 10,
      background: '#FFF',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    fileInput: {
      display: 'none',
    },
    riskGroup: {
      marginBottom: 16,
      padding: 12,
      background: '#FFF',
      border: '1px solid #EAECF0',
      borderRadius: 8,
    },
    riskItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: '1px solid #F2F4F7',
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    riskSelect: {
      padding: '6px 12px',
      border: '1px solid #D0D5DD',
      borderRadius: 6,
      fontSize: 14,
      minWidth: 100,
    },
  })
);

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 900,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
  maxHeight: '90vh',
  overflow: 'auto',
};

const SHOW_RISK_KEYS = [
  'show_scores_1. Adult Sexual Explicit Risk',
  'show_scores_2. Arms & Ammunition Risk',
  'show_scores_3. Crime & Violation of Human Rights Risk',
  'show_scores_4. Death, Injury or Military Conflict Risk',
  'show_scores_5 & 9. Online Piracy & Spam Risk',
  'show_scores_6. Hate Speech & Acts of Aggression Risk',
  'show_scores_7. Obscenity & Profanity Risk',
  'show_scores_8. Illegal Drugs/ Tobacco/e-Cigarettes/Vaping/Alcohol Risk',
  'show_scores_10. Terrorism Risk',
  'show_scores_11. Debated Sensitive Social Issues (Political issues) Risk',
  'show_scores_GAMBLING Risk',
  'show_scores_OCCULT Risk',
  'show_scores_NATURAL DISASTER Risk',
];

const EPISODE_RISK_KEYS = [
  'episode_scores_1. Adult Sexual Explicit Risk',
  'episode_scores_2. Arms & Ammunition Risk',
  'episode_scores_3. Crime & Violation of Human Rights Risk',
  'episode_scores_4. Death, Injury or Military Conflict Risk',
  'episode_scores_5 & 9. Online Piracy & Spam Risk',
  'episode_scores_6. Hate Speech & Acts of Aggression Risk',
  'episode_scores_7. Obscenity & Profanity Risk',
  'episode_scores_8. Illegal Drugs/ Tobacco/e-Cigarettes/Vaping/Alcohol Risk',
  'episode_scores_10. Terrorism Risk',
  'episode_scores_11. Debated Sensitive Social Issues (Political issues) Risk',
  'episode_scores_GAMBLING Risk',
  'episode_scores_OCCULT Risk',
  'episode_scores_NATURAL DISASTER Risk',
];

// Initialize default preferences with all risks set to "High"
const getDefaultPreferences = (): Record<string, string> => {
  const prefs: Record<string, string> = {};
  [...SHOW_RISK_KEYS, ...EPISODE_RISK_KEYS].forEach((key) => {
    prefs[key] = 'High';
  });
  return prefs;
};

const defaultPreferenceData: PreferenceData = {
  RISK_ORDER: ['Low', 'Medium', 'High'],
  preferences: getDefaultPreferences(),
  GENRE_EXCLUSIONS: [],
  GENRE_EXCLUSION_BYPASS_RULES: [],
  SHOW_EXCLUSIONS: [],
  SHOW_INCLUSIONS_FORCE_ALIGN0: [],
  IAB_TOP_EXCLUSION_RULES: [],
  GENRE_RISK_OVERRIDES: [],
  LANGUAGE_POLICY: [],
};

interface PreferenceFormModalProps {
  open: boolean;
  closeModal: () => void;
  preference?: Preference | null;
  alignmentType?: AlignmentType;
}

export const PreferenceFormModal = ({
  open,
  closeModal,
  preference,
  alignmentType,
}: PreferenceFormModalProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const { handleError, handleSuccess } = useNotification();
  const { genres, iabCategories } = useDashboardFilters();

  const [name, setName] = useState('');
  const [dateColumnOverride, setDateColumnOverride] = useState(false);
  const [preferenceData, setPreferenceData] = useState<PreferenceData>(
    defaultPreferenceData
  );
  const [loading, setLoading] = useState(false);
  const [showGenreSelector, setShowGenreSelector] = useState(false);
  const [showIABSelector, setShowIABSelector] = useState(false);
  const [showShowSelector, setShowShowSelector] = useState<
    'exclusions' | 'inclusions' | false
  >(false);
  const [showRiskColumnSelector, setShowRiskColumnSelector] = useState<
    number | null
  >(null);
  const [allShowNames, setAllShowNames] = useState<string[]>([]);
  const [loadingShows, setLoadingShows] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [preferenceShowCsvSlot, setPreferenceShowCsvSlot] = useState<
    'exclusions' | 'inclusions' | null
  >(null);
  const jsonFileInputRef = useRef<HTMLInputElement>(null);

  const isEditMode = !!preference;

  const loadShowNames = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = !!options?.silent;
      if (!silent) {
        setLoadingShows(true);
      }
      try {
        const { showNames } = await getFilteredGarmShowNames({});
        setAllShowNames(showNames || []);
      } catch (error) {
        handleError(error, 'Failed to load show names');
      } finally {
        if (!silent) {
          setLoadingShows(false);
        }
      }
    },
    [handleError]
  );

  useEffect(() => {
    if (open) {
      loadShowNames();
    }
  }, [open, loadShowNames]);

  useEffect(() => {
    if (open) {
      if (preference) {
        setName(preference.name);
        // Ensure preferences are initialized with defaults if missing
        const data = { ...preference.data };
        if (!data.preferences || Object.keys(data.preferences).length === 0) {
          data.preferences = getDefaultPreferences();
        } else {
          // Ensure all risk keys exist, defaulting to "High" if missing
          const allKeys = [...SHOW_RISK_KEYS, ...EPISODE_RISK_KEYS];
          allKeys.forEach((key) => {
            if (!data.preferences[key]) {
              data.preferences[key] = 'High';
            }
          });
        }
        // Convert old LANGUAGE_POLICY format to new format
        if (
          data.LANGUAGE_POLICY &&
          typeof data.LANGUAGE_POLICY === 'object' &&
          !Array.isArray(data.LANGUAGE_POLICY)
        ) {
          const oldPolicy = data.LANGUAGE_POLICY as any;
          if (oldPolicy.english_only) {
            data.LANGUAGE_POLICY = ['en'];
          } else {
            data.LANGUAGE_POLICY = [];
          }
        } else if (!Array.isArray(data.LANGUAGE_POLICY)) {
          data.LANGUAGE_POLICY = [];
        }
        data.IAB_TOP_EXCLUSION_RULES = normalizeIabTopExclusionRules(
          data.IAB_TOP_EXCLUSION_RULES
        );
        data.GENRE_RISK_OVERRIDES = normalizeGenreRiskOverrides(
          data.GENRE_RISK_OVERRIDES
        );
        data.GENRE_EXCLUSION_BYPASS_RULES = normalizeGenreExclusionBypassRules(
          data.GENRE_EXCLUSION_BYPASS_RULES
        );
        setPreferenceData(data);
      } else {
        setName('');
        setDateColumnOverride(false);
        setPreferenceData(defaultPreferenceData);
      }
    }
  }, [open, preference]);

  const handleJsonFile = (file: File) => {
    if (file.size > 10485760) {
      handleError({}, 'Please upload JSON file less than 10 MB.');
      return;
    }

    if (!file.type.includes('json') && !file.name.endsWith('.json')) {
      handleError({}, 'Please upload a valid JSON file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const parsed = JSON.parse(text) as PreferenceData;

        // Ensure preferences are initialized
        if (
          !parsed.preferences ||
          Object.keys(parsed.preferences).length === 0
        ) {
          parsed.preferences = getDefaultPreferences();
        } else {
          // Ensure all risk keys exist, defaulting to "High" if missing
          const allKeys = [...SHOW_RISK_KEYS, ...EPISODE_RISK_KEYS];
          allKeys.forEach((key) => {
            if (!parsed.preferences[key]) {
              parsed.preferences[key] = 'High';
            }
          });
        }

        parsed.IAB_TOP_EXCLUSION_RULES = normalizeIabTopExclusionRules(
          parsed.IAB_TOP_EXCLUSION_RULES
        );
        parsed.GENRE_RISK_OVERRIDES = normalizeGenreRiskOverrides(
          parsed.GENRE_RISK_OVERRIDES
        );
        parsed.GENRE_EXCLUSION_BYPASS_RULES =
          normalizeGenreExclusionBypassRules(
            parsed.GENRE_EXCLUSION_BYPASS_RULES
          );

        setPreferenceData(parsed);
        handleSuccess('Preference data loaded from JSON file');
      } catch (error) {
        handleError(
          error,
          'Failed to parse JSON file. Please check the format.'
        );
      }
    };
    reader.readAsText(file);
  };

  const handleDrag = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleJsonFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleJsonFile(e.target.files[0]);
    }
  };

  const handleUploadClick = () => {
    if (jsonFileInputRef.current) {
      jsonFileInputRef.current.click();
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      handleError({}, 'Please enter a preference name');
      return;
    }

    // Prepare preference data - filter episode risks if alignmentType is 'show'
    let finalPreferenceData = { ...preferenceData };

    if (alignmentType === 'show') {
      // Remove episode risk preferences when alignmentType is 'show'
      const filteredPreferences: Record<string, string> = {};
      SHOW_RISK_KEYS.forEach((key) => {
        if (preferenceData.preferences[key]) {
          filteredPreferences[key] = preferenceData.preferences[key];
        }
      });
      finalPreferenceData = {
        ...preferenceData,
        preferences: filteredPreferences,
      };
    } else {
      // Ensure all risk preferences are set (cannot be null) for show_episode or undefined
      const allKeys = [...SHOW_RISK_KEYS, ...EPISODE_RISK_KEYS];
      const missingKeys = allKeys.filter(
        (key) => !preferenceData.preferences[key]
      );
      if (missingKeys.length > 0) {
        // Set missing keys to "High" as default
        const updatedPreferences = { ...preferenceData.preferences };
        missingKeys.forEach((key) => {
          updatedPreferences[key] = 'High';
        });
        finalPreferenceData = {
          ...preferenceData,
          preferences: updatedPreferences,
        };
      }
    }

    setLoading(true);
    try {
      if (isEditMode && preference) {
        await updatePreference(preference.id, {
          name,
          data: finalPreferenceData,
        });
        handleSuccess('Preference updated successfully');
      } else {
        await createPreference({
          name,
          data: finalPreferenceData,
          date_column_override: dateColumnOverride,
        });
        handleSuccess('Preference created successfully');
      }
      closeModal();
    } catch (error) {
      handleError(
        error,
        `Failed to ${isEditMode ? 'update' : 'create'} preference`
      );
    } finally {
      setLoading(false);
    }
  };

  const addGenreExceptionRule = () => {
    setPreferenceData({
      ...preferenceData,
      GENRE_EXCLUSION_BYPASS_RULES: [
        ...preferenceData.GENRE_EXCLUSION_BYPASS_RULES,
        { genre: '', risk_category: '', risk_level: 'High' },
      ],
    });
  };

  const updateGenreExceptionRule = (
    index: number,
    field: 'genre' | 'risk_category' | 'risk_level',
    value: string
  ) => {
    const rules = [...preferenceData.GENRE_EXCLUSION_BYPASS_RULES];
    rules[index] = { ...rules[index], [field]: value };
    setPreferenceData({
      ...preferenceData,
      GENRE_EXCLUSION_BYPASS_RULES: rules,
    });
  };

  const removeGenreExceptionRule = (index: number) => {
    setPreferenceData({
      ...preferenceData,
      GENRE_EXCLUSION_BYPASS_RULES:
        preferenceData.GENRE_EXCLUSION_BYPASS_RULES.filter(
          (_, i) => i !== index
        ),
    });
  };

  const addRiskOverride = () => {
    setPreferenceData({
      ...preferenceData,
      GENRE_RISK_OVERRIDES: [
        ...preferenceData.GENRE_RISK_OVERRIDES,
        { pattern: '', columns: [], max_risk: 'High' },
      ],
    });
  };

  const updateRiskOverride = (
    index: number,
    field: 'pattern' | 'max_risk',
    value: string
  ) => {
    const overrides = [...preferenceData.GENRE_RISK_OVERRIDES];
    overrides[index] = { ...overrides[index], [field]: value };
    setPreferenceData({
      ...preferenceData,
      GENRE_RISK_OVERRIDES: overrides,
    });
  };

  const removeRiskOverride = (index: number) => {
    setPreferenceData({
      ...preferenceData,
      GENRE_RISK_OVERRIDES: preferenceData.GENRE_RISK_OVERRIDES.filter(
        (_, i) => i !== index
      ),
    });
  };

  return (
    <>
      <Modal
        open={open}
        aria-labelledby='preference-form-modal-title'
        aria-describedby='preference-form-modal-description'
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 800,
        }}
      >
        <Box sx={{ ...modalStyle }}>
          <Box className={classes.header}>
            <Typography
              className={textClasses.lgBold}
              style={{ color: '#101828' }}
            >
              {isEditMode ? 'Edit Preference' : 'Create Preference'}
            </Typography>
            <CancelIcon
              cursor='pointer'
              width={24}
              height={24}
              onClick={closeModal}
            />
          </Box>
          <Box className={classes.content}>
            <Box className={classes.inputField}>
              <Typography variant='body1' style={{ fontSize: 14 }}>
                Preference Name
              </Typography>
              <TextInput
                default='Enter preference name'
                value={name}
                onChange={setName}
                className={classes.description}
              />
            </Box>

            {!isEditMode && (
              <Box className={classes.inputField}>
                <Checkbox
                  label='Date column override'
                  checked={dateColumnOverride}
                  onChange={(checked) => setDateColumnOverride(checked)}
                />
              </Box>
            )}

            {/* JSON Upload */}
            <Box className={classes.inputField}>
              <Box
                className={`${classes.dragDropArea} ${
                  dragActive ? 'drag-active' : ''
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  ref={jsonFileInputRef}
                  type='file'
                  accept='.json,application/json'
                  className={classes.fileInput}
                  onChange={handleFileChange}
                />
                <div className={classes.uploadIcon}>
                  <UploadIcon width={20} height={20} />
                </div>
                <Typography
                  className={textClasses.smBold}
                  style={{ color: '#344054', marginBottom: 4 }}
                >
                  <Button
                    variant='text'
                    fontSize='sm'
                    height={20}
                    onClick={handleUploadClick}
                  >
                    <span style={{ color: '#5EA1F2' }}>Click to upload</span>
                  </Button>
                  {' or drag and drop'}
                </Typography>
                <Typography
                  className={textClasses.xsRegular}
                  style={{ color: '#475467' }}
                >
                  JSON file
                </Typography>
              </Box>
            </Box>

            {/* Risk Preferences */}
            <Box className={classes.section}>
              <Typography
                className={classes.sectionTitle}
                variant='body1'
                style={{ fontSize: 16, marginBottom: 16 }}
              >
                Risk Preferences
              </Typography>

              {/* Show Scores Group */}
              <Box className={classes.riskGroup}>
                <Typography
                  variant='body2'
                  style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}
                >
                  Show Scores
                </Typography>
                {SHOW_RISK_KEYS.map((key) => (
                  <Box key={key} className={classes.riskItem}>
                    <Typography style={{ fontSize: 14, flex: 1 }}>
                      {key}
                    </Typography>
                    <select
                      value={preferenceData.preferences[key] || 'High'}
                      onChange={(e) => {
                        const newPreferences = {
                          ...preferenceData.preferences,
                        };
                        newPreferences[key] = e.target.value;
                        setPreferenceData({
                          ...preferenceData,
                          preferences: newPreferences,
                        });
                      }}
                      className={classes.riskSelect}
                    >
                      {preferenceData.RISK_ORDER.map((risk) => (
                        <option key={risk} value={risk}>
                          {risk}
                        </option>
                      ))}
                    </select>
                  </Box>
                ))}
              </Box>

              {/* Episode Scores Group - Only show when alignmentType is 'show_episode' or undefined */}
              {alignmentType !== 'show' && (
                <Box className={classes.riskGroup} style={{ marginTop: 16 }}>
                  <Typography
                    variant='body2'
                    style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}
                  >
                    Episode Scores
                  </Typography>
                  {EPISODE_RISK_KEYS.map((key) => (
                    <Box key={key} className={classes.riskItem}>
                      <Typography style={{ fontSize: 14, flex: 1 }}>
                        {key}
                      </Typography>
                      <select
                        value={preferenceData.preferences[key] || 'High'}
                        onChange={(e) => {
                          const newPreferences = {
                            ...preferenceData.preferences,
                          };
                          newPreferences[key] = e.target.value;
                          setPreferenceData({
                            ...preferenceData,
                            preferences: newPreferences,
                          });
                        }}
                        className={classes.riskSelect}
                      >
                        {preferenceData.RISK_ORDER.map((risk) => (
                          <option key={risk} value={risk}>
                            {risk}
                          </option>
                        ))}
                      </select>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Genre Exclusions */}
            <Box className={classes.section}>
              <Typography
                className={classes.sectionTitle}
                variant='body1'
                style={{ fontSize: 16 }}
              >
                Genre Exclusions
              </Typography>
              <Button
                variant='outlined'
                height={52}
                onClick={() => {
                  setShowGenreSelector(true);
                }}
                className={classes.selectorButton}
              >
                <Typography
                  style={{ fontSize: 16, fontWeight: 400, color: '#344054' }}
                >
                  {preferenceData.GENRE_EXCLUSIONS.length > 0
                    ? `${preferenceData.GENRE_EXCLUSIONS.length} genre(s) selected`
                    : 'Select genres to exclude'}
                </Typography>
              </Button>
              {preferenceData.GENRE_EXCLUSIONS.length > 0 && (
                <Box className={classes.selectedItems}>
                  {preferenceData.GENRE_EXCLUSIONS.map((genre) => (
                    <Box key={genre} className={classes.selectedItem}>
                      <span>{genre}</span>
                      <span
                        className={classes.removeButton}
                        onClick={() => {
                          setPreferenceData({
                            ...preferenceData,
                            GENRE_EXCLUSIONS:
                              preferenceData.GENRE_EXCLUSIONS.filter(
                                (g) => g !== genre
                              ),
                          });
                        }}
                      >
                        ×
                      </span>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Genre Exception Rules */}
            <Box className={classes.section}>
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                marginBottom={1}
              >
                <Typography
                  className={classes.sectionTitle}
                  variant='body1'
                  style={{ fontSize: 16 }}
                >
                  Genre Exception Rules
                </Typography>
                <Button
                  variant='outlined'
                  height={32}
                  onClick={addGenreExceptionRule}
                  style={{ minWidth: 'auto', padding: '4px 12px' }}
                >
                  <AddIcon style={{ fontSize: 18 }} />
                </Button>
              </Box>
              {preferenceData.GENRE_EXCLUSION_BYPASS_RULES.map(
                (rule, index) => (
                  <Box key={index} className={classes.ruleItem}>
                    <Box className={classes.ruleField}>
                      <Typography className={classes.ruleFieldLabel}>
                        Allow Genre
                      </Typography>
                      <select
                        value={rule.genre}
                        onChange={(e) =>
                          updateGenreExceptionRule(
                            index,
                            'genre',
                            e.target.value
                          )
                        }
                        className={classes.ruleInput}
                      >
                        <option value=''>Select genre</option>
                        {genres.map((genre) => (
                          <option key={genre} value={genre}>
                            {genre}
                          </option>
                        ))}
                      </select>
                    </Box>
                    <Box className={classes.ruleField}>
                      <Typography className={classes.ruleFieldLabel}>
                        Risk Category for Exception
                      </Typography>
                      <select
                        value={rule.risk_category}
                        onChange={(e) =>
                          updateGenreExceptionRule(
                            index,
                            'risk_category',
                            e.target.value
                          )
                        }
                        className={classes.ruleInput}
                      >
                        <option value=''>Select risk category</option>
                        {RISK_CATEGORY_OPTIONS.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </Box>
                    <Box
                      className={classes.ruleField}
                      style={{ flex: 0.6, minWidth: 120 }}
                    >
                      <Typography className={classes.ruleFieldLabel}>
                        Risk Level to Allow
                      </Typography>
                      <select
                        value={rule.risk_level}
                        onChange={(e) =>
                          updateGenreExceptionRule(
                            index,
                            'risk_level',
                            e.target.value
                          )
                        }
                        className={classes.ruleInput}
                      >
                        {preferenceData.RISK_ORDER.map((risk) => (
                          <option key={risk} value={risk}>
                            {risk}
                          </option>
                        ))}
                      </select>
                    </Box>
                    <IconButton
                      size='small'
                      onClick={() => removeGenreExceptionRule(index)}
                      style={{ padding: 4, marginBottom: 4 }}
                    >
                      <DeleteIcon style={{ fontSize: 18, color: '#B42318' }} />
                    </IconButton>
                  </Box>
                )
              )}
            </Box>

            {/* Show Exclusions */}
            <Box className={classes.section}>
              <Typography
                className={classes.sectionTitle}
                variant='body1'
                style={{ fontSize: 16 }}
              >
                Show Exclusions
              </Typography>
              <Button
                variant='outlined'
                height={52}
                onClick={() => {
                  setShowShowSelector('exclusions');
                }}
                className={classes.selectorButton}
                disabled={loadingShows}
              >
                <Typography
                  style={{ fontSize: 16, fontWeight: 400, color: '#344054' }}
                >
                  {loadingShows
                    ? 'Loading shows...'
                    : preferenceData.SHOW_EXCLUSIONS.length > 0
                    ? `${preferenceData.SHOW_EXCLUSIONS.length} show(s) selected`
                    : 'Select shows to exclude'}
                </Typography>
              </Button>
              <Box
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  marginTop: 10,
                  alignItems: 'center',
                }}
              >
                <Button
                  variant='outlined'
                  height={40}
                  onClick={() => setPreferenceShowCsvSlot('exclusions')}
                  disabled={loadingShows}
                >
                  Import CSV
                </Button>
                <Button
                  variant='outlined'
                  height={40}
                  onClick={() =>
                    setPreferenceData({
                      ...preferenceData,
                      SHOW_EXCLUSIONS: [],
                    })
                  }
                  disabled={
                    loadingShows || preferenceData.SHOW_EXCLUSIONS.length === 0
                  }
                  style={{
                    ...(preferenceData.SHOW_EXCLUSIONS.length > 0
                      ? { color: '#B42318', borderColor: '#FECDCA' }
                      : {}),
                  }}
                >
                  Remove all
                </Button>
              </Box>
              {preferenceData.SHOW_EXCLUSIONS.length > 0 && (
                <Box className={classes.selectedItems}>
                  {preferenceData.SHOW_EXCLUSIONS.map((show) => (
                    <Box key={show} className={classes.selectedItem}>
                      <span>{show}</span>
                      <span
                        className={classes.removeButton}
                        onClick={() => {
                          setPreferenceData({
                            ...preferenceData,
                            SHOW_EXCLUSIONS:
                              preferenceData.SHOW_EXCLUSIONS.filter(
                                (s) => s !== show
                              ),
                          });
                        }}
                      >
                        ×
                      </span>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Show Inclusions Force Align0 */}
            <Box className={classes.section}>
              <Typography
                className={classes.sectionTitle}
                variant='body1'
                style={{ fontSize: 16 }}
              >
                Show Inclusions (Force Align0)
              </Typography>
              <Button
                variant='outlined'
                height={52}
                onClick={() => {
                  setShowShowSelector('inclusions');
                }}
                className={classes.selectorButton}
                disabled={loadingShows}
              >
                <Typography
                  style={{ fontSize: 16, fontWeight: 400, color: '#344054' }}
                >
                  {loadingShows
                    ? 'Loading shows...'
                    : preferenceData.SHOW_INCLUSIONS_FORCE_ALIGN0.length > 0
                    ? `${preferenceData.SHOW_INCLUSIONS_FORCE_ALIGN0.length} show(s) selected`
                    : 'Select shows to include'}
                </Typography>
              </Button>
              <Box
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  marginTop: 10,
                  alignItems: 'center',
                }}
              >
                <Button
                  variant='outlined'
                  height={40}
                  onClick={() => setPreferenceShowCsvSlot('inclusions')}
                  disabled={loadingShows}
                >
                  Import CSV
                </Button>
                <Button
                  variant='outlined'
                  height={40}
                  onClick={() =>
                    setPreferenceData({
                      ...preferenceData,
                      SHOW_INCLUSIONS_FORCE_ALIGN0: [],
                    })
                  }
                  disabled={
                    loadingShows ||
                    preferenceData.SHOW_INCLUSIONS_FORCE_ALIGN0.length === 0
                  }
                  style={{
                    ...(preferenceData.SHOW_INCLUSIONS_FORCE_ALIGN0.length > 0
                      ? { color: '#B42318', borderColor: '#FECDCA' }
                      : {}),
                  }}
                >
                  Remove all
                </Button>
              </Box>
              {preferenceData.SHOW_INCLUSIONS_FORCE_ALIGN0.length > 0 && (
                <Box className={classes.selectedItems}>
                  {preferenceData.SHOW_INCLUSIONS_FORCE_ALIGN0.map((show) => (
                    <Box key={show} className={classes.selectedItem}>
                      <span>{show}</span>
                      <span
                        className={classes.removeButton}
                        onClick={() => {
                          setPreferenceData({
                            ...preferenceData,
                            SHOW_INCLUSIONS_FORCE_ALIGN0:
                              preferenceData.SHOW_INCLUSIONS_FORCE_ALIGN0.filter(
                                (s) => s !== show
                              ),
                          });
                        }}
                      >
                        ×
                      </span>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* IAB Top Exclusion Rules */}
            <Box className={classes.section}>
              <Typography
                className={classes.sectionTitle}
                variant='body1'
                style={{ fontSize: 16 }}
              >
                IAB Top Exclusion Rules
              </Typography>
              <Button
                variant='outlined'
                height={52}
                onClick={() => {
                  setShowIABSelector(true);
                }}
                className={classes.selectorButton}
              >
                <Typography
                  style={{ fontSize: 16, fontWeight: 400, color: '#344054' }}
                >
                  {preferenceData.IAB_TOP_EXCLUSION_RULES.length > 0
                    ? `${preferenceData.IAB_TOP_EXCLUSION_RULES.length} IAB category(ies) selected`
                    : 'Select IAB categories to exclude'}
                </Typography>
              </Button>
              {preferenceData.IAB_TOP_EXCLUSION_RULES.length > 0 && (
                <Box className={classes.selectedItems}>
                  {preferenceData.IAB_TOP_EXCLUSION_RULES.map((iabCategory) => (
                    <Box key={iabCategory} className={classes.selectedItem}>
                      <span>{iabCategory}</span>
                      <span
                        className={classes.removeButton}
                        onClick={() => {
                          setPreferenceData({
                            ...preferenceData,
                            IAB_TOP_EXCLUSION_RULES:
                              preferenceData.IAB_TOP_EXCLUSION_RULES.filter(
                                (c) => c !== iabCategory
                              ),
                          });
                        }}
                      >
                        ×
                      </span>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Genre Risk Overrides */}
            <Box className={classes.section}>
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                marginBottom={1}
              >
                <Typography
                  className={classes.sectionTitle}
                  variant='body1'
                  style={{ fontSize: 16 }}
                >
                  Genre Risk Overrides
                </Typography>
                <Button
                  variant='outlined'
                  height={32}
                  onClick={addRiskOverride}
                  style={{ minWidth: 'auto', padding: '4px 12px' }}
                >
                  <AddIcon style={{ fontSize: 18 }} />
                </Button>
              </Box>
              {preferenceData.GENRE_RISK_OVERRIDES.map((override, index) => (
                <Box key={index} className={classes.riskOverrideItem}>
                  <Typography
                    variant='body2'
                    style={{ fontSize: 12, color: '#667085', marginBottom: 6 }}
                  >
                    Genre pattern (regex)
                  </Typography>
                  <Box className={classes.riskOverrideHeader}>
                    <TextInput
                      value={override.pattern}
                      onChange={(v) => updateRiskOverride(index, 'pattern', v)}
                      className={classes.ruleInput}
                      fullWidth
                      style={{ flex: 1 }}
                      placeholder='e.g. \\btrue crime\\b'
                    />
                    <select
                      value={override.max_risk}
                      onChange={(e) =>
                        updateRiskOverride(index, 'max_risk', e.target.value)
                      }
                      className={classes.ruleInput}
                      style={{ flex: 0.35, minWidth: 120 }}
                    >
                      <option value='Low'>Low</option>
                      <option value='Medium'>Medium</option>
                      <option value='High'>High</option>
                    </select>
                    <IconButton
                      size='small'
                      onClick={() => removeRiskOverride(index)}
                      style={{ padding: 4 }}
                    >
                      <DeleteIcon style={{ fontSize: 18, color: '#B42318' }} />
                    </IconButton>
                  </Box>
                  <Box
                    display='flex'
                    flexWrap='wrap'
                    alignItems='center'
                    style={{ marginTop: 8, gap: 8 }}
                  >
                    <Button
                      variant='outlined'
                      height={32}
                      onClick={() => setShowRiskColumnSelector(index)}
                      style={{ minWidth: 'auto', padding: '4px 12px' }}
                    >
                      <Typography style={{ fontSize: 13 }}>
                        {override.columns?.length
                          ? `${override.columns.length} risk column(s)`
                          : 'Select risk columns'}
                      </Typography>
                    </Button>
                  </Box>
                  {(override.columns?.length ?? 0) > 0 && (
                    <Box className={classes.riskOverrideColumns}>
                      {override.columns.map((col) => (
                        <span key={col} className={classes.columnChip}>
                          {col}
                        </span>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {/* Language Policy */}
            <Box className={classes.section}>
              <Typography
                className={classes.sectionTitle}
                variant='body1'
                style={{ fontSize: 16 }}
              >
                Language Policy
              </Typography>
              <Button
                variant='outlined'
                height={52}
                onClick={() => {
                  setShowLanguageSelector(true);
                }}
                className={classes.selectorButton}
              >
                <Typography
                  style={{ fontSize: 16, fontWeight: 400, color: '#344054' }}
                >
                  {preferenceData.LANGUAGE_POLICY.length > 0
                    ? `${preferenceData.LANGUAGE_POLICY.length} language(s) selected`
                    : 'Select languages'}
                </Typography>
              </Button>
              {preferenceData.LANGUAGE_POLICY.length > 0 && (
                <Box className={classes.selectedItems}>
                  {preferenceData.LANGUAGE_POLICY.map((langCode) => {
                    // Find language name from All_Languages
                    const findLanguageName = (
                      items: typeof All_Languages,
                      code: string
                    ): string | null => {
                      for (const item of items) {
                        if (item.id === code) {
                          return item.name;
                        }
                        if (item.children) {
                          const found = findLanguageName(item.children, code);
                          if (found) return found;
                        }
                      }
                      return null;
                    };
                    const langName =
                      findLanguageName(All_Languages, langCode) || langCode;
                    return (
                      <Box key={langCode} className={classes.selectedItem}>
                        <span>{langName}</span>
                        <span
                          className={classes.removeButton}
                          onClick={() => {
                            setPreferenceData({
                              ...preferenceData,
                              LANGUAGE_POLICY:
                                preferenceData.LANGUAGE_POLICY.filter(
                                  (l) => l !== langCode
                                ),
                            });
                          }}
                        >
                          ×
                        </span>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
          <Box className={classes.buttons}>
            <Button
              title='Cancel'
              variant='outlined'
              height={46}
              onClick={closeModal}
            />
            <Button height={46} onClick={handleSave} disabled={loading}>
              {loading ? (
                <CircularProgress style={{ color: '#fff' }} size={24} />
              ) : (
                <span>{isEditMode ? 'Update' : 'Create'}</span>
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      <GenreSelector
        open={showGenreSelector}
        onClose={() => setShowGenreSelector(false)}
        genres={genres}
        selectedGenres={preferenceData.GENRE_EXCLUSIONS}
        onSelect={(selected) => {
          setPreferenceData({
            ...preferenceData,
            GENRE_EXCLUSIONS: selected,
          });
          setShowGenreSelector(false);
        }}
      />

      <IABSelector
        open={showIABSelector}
        onClose={() => setShowIABSelector(false)}
        iabCategories={iabCategories}
        selectedIABCategories={preferenceData.IAB_TOP_EXCLUSION_RULES}
        onSelect={(selected) => {
          setPreferenceData({
            ...preferenceData,
            IAB_TOP_EXCLUSION_RULES: selected,
          });
          setShowIABSelector(false);
        }}
      />

      <ShowSelector
        open={!!showShowSelector}
        onClose={() => setShowShowSelector(false)}
        showNames={allShowNames}
        selectedShows={
          showShowSelector === 'exclusions'
            ? preferenceData.SHOW_EXCLUSIONS
            : preferenceData.SHOW_INCLUSIONS_FORCE_ALIGN0
        }
        onSelect={(selected) => {
          if (showShowSelector === 'exclusions') {
            setPreferenceData({
              ...preferenceData,
              SHOW_EXCLUSIONS: selected,
            });
          } else {
            setPreferenceData({
              ...preferenceData,
              SHOW_INCLUSIONS_FORCE_ALIGN0: selected,
            });
          }
          setShowShowSelector(false);
        }}
      />

      <PreferenceShowCsvImportModal
        open={preferenceShowCsvSlot !== null}
        onClose={() => setPreferenceShowCsvSlot(null)}
        onImported={(names, warnings = []) => {
          if (names.length === 0) {
            handleError({}, 'No matched shows to add from the import.');
            return;
          }
          const slot = preferenceShowCsvSlot;
          setPreferenceData((prev) => {
            if (slot === 'exclusions') {
              return {
                ...prev,
                SHOW_EXCLUSIONS: Array.from(
                  new Set([...prev.SHOW_EXCLUSIONS, ...names])
                ),
              };
            }
            return {
              ...prev,
              SHOW_INCLUSIONS_FORCE_ALIGN0: Array.from(
                new Set([...prev.SHOW_INCLUSIONS_FORCE_ALIGN0, ...names])
              ),
            };
          });
          handleSuccess(
            `${names.length} show(s) matched and added to your list.${
              warnings.some(Boolean)
                ? ' Some rows were skipped or partially processed.'
                : ''
            }`
          );
          void loadShowNames({ silent: true });
        }}
      />

      {showRiskColumnSelector !== null && (
        <RiskColumnSelector
          open={showRiskColumnSelector !== null}
          onClose={() => setShowRiskColumnSelector(null)}
          selectedColumns={
            preferenceData.GENRE_RISK_OVERRIDES[showRiskColumnSelector]
              ?.columns || []
          }
          onSelect={(selected) => {
            const overrides = [...preferenceData.GENRE_RISK_OVERRIDES];
            overrides[showRiskColumnSelector].columns = selected;
            setPreferenceData({
              ...preferenceData,
              GENRE_RISK_OVERRIDES: overrides,
            });
            setShowRiskColumnSelector(null);
          }}
        />
      )}

      <LanguageSelector
        open={showLanguageSelector}
        onClose={() => setShowLanguageSelector(false)}
        selectedLanguages={preferenceData.LANGUAGE_POLICY}
        onSelect={(selected) => {
          setPreferenceData({
            ...preferenceData,
            LANGUAGE_POLICY: selected,
          });
          setShowLanguageSelector(false);
        }}
      />
    </>
  );
};
